import { Dialog } from '@mui/material';
import 'firebase/firestore';
import queryString from 'query-string';
import React, { createContext, Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { trackUserSession } from '../../apis/trackUserSession';
import db_config from '../../configs/db_config';
import { BillStatus, OrderItemStatus } from '../../consts';
import useCreateOrder from '../../hook/useCreateOrder';
import useGetListUsers from '../../hook/useGetListUsers';
import { CreateOrderDTO, FoodItem, Order, User_Rider } from '../../interfaces/types';
import { db } from '../../libs/firebase';
import { generateOrderId } from '../../utils';
import { useSession } from '../SessionContext';
import { useSystemContext } from '../SystemContext/SystemContext';

interface OrderContextType {
  updateOrderDetails: (details: CreateOrderDTO) => void;
  updateOrderId: (id: string) => Promise<string>;
  createOrder: (orderDetails: CreateOrderDTO, orderId: string) => Promise<boolean>;
  orderDetails: CreateOrderDTO;
  setOrderDetails: Dispatch<SetStateAction<CreateOrderDTO>>;
  loadingOrder: boolean;
  orders: Order[];
  orderId: string | undefined;
  errorOrder: any;
  setIsCreate: Dispatch<SetStateAction<boolean>>;
}

const OrderContext = createContext<OrderContextType>({
  updateOrderDetails: () => {},
  updateOrderId: async (id: string) => {
    return '';
  },
  orderDetails: {
    order_status: OrderItemStatus.PREPARING,
    order_table_number: '',
    foods: [] as FoodItem[],
    promotion_foods: [] as FoodItem[],
    bill_status: BillStatus.NOT_CLEARED,
    payment_method: '',
    restaurant_id: '',
    promotion_restaurant_id: '',
    seat_type: '',
    seat_number: '',
  },
  setOrderDetails: () => {},
  loadingOrder: false,
  orders: [],
  orderId: undefined,
  errorOrder: false,
  createOrder: async () => {
    return false;
  },
  setIsCreate: () => {},
});

export const useOrderContext = () => React.useContext(OrderContext);

export const OrderProvider: React.FC = ({ children }) => {
  const [riders, setRiders] = useState<{ [id: string]: User_Rider }>({});
  const [orders, setOrders] = useState<Order[]>([]);
  const { t } = useTranslation();
  const [orderDetails, setOrderDetails] = useState<CreateOrderDTO>({
    order_status: OrderItemStatus.PREPARING,
    order_table_number: '',
    foods: [] as FoodItem[],
    promotion_foods: [] as FoodItem[],
    bill_status: BillStatus.NOT_CLEARED,
    payment_method: '',
    restaurant_id: '',
    seat_type: '',
    seat_number: '',
  });
  const [orderId, setOrderId] = useState<string>();
  const [loadingOrder, setLoadingOrder] = useState(true);
  const [loadingRiders, setLoadingRiders] = useState(true);
  const [isCreate, setIsCreate] = React.useState(false);
  const [openDialog, setOpenDialog] = React.useState(false);
  const { data: listUsers } = useGetListUsers();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = queryString.parse(location.search);
  const { session, sessionLoading } = useSession();
  const { seatInfo, stadiumInfo } = useSystemContext();

  const handleCloseDialog = async () => {
    localStorage.removeItem('received_order');
    const currentOrderId = sessionStorage.getItem('orderId');

    if ((!currentOrderId || orders.findIndex((o) => o.id === currentOrderId) > -1) && seatInfo?.id) {
      await updateOrderId();
    }
    const { tab, ...cleanedQueryParams } = queryParams;
    const neworderDetailLink = queryString.stringifyUrl({
      url: `/neworder`,
      query: { ...cleanedQueryParams },
    });
    navigate(neworderDetailLink);

    setOpenDialog(false);
  };

  const { createOrder, errorOrder } = useCreateOrder();

  const updateOrderDetails = (details: CreateOrderDTO) => {
    setOrderDetails(details);
  };

  const updateOrderId = async () => {
    const orderNewId = await generateOrderId(seatInfo!, stadiumInfo?.id);
    setOrderId(orderNewId);
    sessionStorage.setItem('orderId', orderNewId);
    return orderNewId;
  };

  const getAllRider = async () => {
    if (!listUsers) {
      setLoadingRiders(false);
      return;
    }
    try {
      const users = await db.collection(db_config.DB_TABLE_STADIUM_MANAGE_USERS).where('role', '==', 'RIDER').get();
      const configSnapshot = await db.collection(db_config.DB_TABLE_STADIUM_CONFIGURATION).doc(db_config.DB_TABLE_STADIUM_CONFIGURATION).get();
      const configData = configSnapshot.data();
      const data: any = {};
      users.docs.forEach((user) => {
        const rider = user.data();
        if (rider?.user_name) {
          const userInfo = listUsers.find((u: any) => u.uid === user.id);
          const gender = userInfo?.gender ?? 0;
          const imageUrl = configData?.rider_url?.[gender] ?? null;
          const riderInfor = {
            name: rider.user_name,
            image: imageUrl,
          };
          data[user.id] = riderInfor;
        }
      });

      setRiders(data);
    } catch (error) {
      console.error('Error stadium_manage_users: ', error);
    } finally {
      setLoadingRiders(false);
    }
  };

  const getOrderHistory = async () => {
    if (seatInfo?.id && !isCreate) {
      await updateOrderId();
    }

    try {
      setLoadingOrder(true);
      const unsubscribe = db
        .collection(db_config.DB_TABLE_ALL_ORDERS)
        .where('session_id', '==', session?.sessionId)
        .orderBy('order_create_time', 'desc')
        .onSnapshot((querySnapshot) => {
          if (querySnapshot.size) {
            const orderHistory: any = [];
            querySnapshot.docChanges().forEach((_change) => {
              if (_change.type === 'modified') {
                const data = _change.doc.data();
                if (data.order_status === OrderItemStatus.DELIVERED) {
                  localStorage.setItem('received_order', 'active');
                  setOpenDialog(true);
                }
              }
            });

            querySnapshot.forEach((doc) => {
              const order = doc.data();
              if (order.is_damaged === true) return;
              let rider = riders[order?.rider_id] ?? null;

              orderHistory.push({ ...order, id: doc.id, rider });
            });

            setOrders(orderHistory);
          }
          setLoadingOrder(false);
        });
      return unsubscribe;
    } catch (error) {
      setLoadingOrder(false);
      console.error('error:', error);
    }
  };

  useEffect(() => {
    getAllRider();
  }, [listUsers]);

  useEffect(() => {
    const fetchOrderHistory = async () => {
      const user_uuid = localStorage.getItem('uuid');
      const received_order = localStorage.getItem('received_order');
      if (received_order === 'active') {
        setOpenDialog(true);
      }
      if (!user_uuid) {
        let retries = 3;
        while (retries > 0) {
          const uuid = uuidv4();
          localStorage.setItem('uuid', uuid);

          const storedUuid = localStorage.getItem('uuid');
          if (storedUuid === uuid) {
            await getOrderHistory();
            if (session?.sessionId) {
              trackUserSession({ uuid, sessionId: session.sessionId, stadiumId: stadiumInfo?.id ?? '', seatId: seatInfo?.id ?? '' });
            }
            break;
          } else {
            retries--;
            console.warn(`Retry storing uuid (${3 - retries}/3)`);
          }
        }
      } else {
        await getOrderHistory();
      }
    };

    if (!loadingRiders && !sessionLoading && !location.pathname.includes('download-ticket')) {
      fetchOrderHistory();
    }

    return () => {};
  }, [loadingRiders, session, sessionLoading, riders]);

  return (
    <OrderContext.Provider
      value={{
        updateOrderDetails,
        createOrder,
        updateOrderId,
        orderDetails,
        setOrderDetails,
        loadingOrder,
        orders,
        errorOrder,
        orderId,
        setIsCreate,
      }}
    >
      {children}
      <Dialog open={openDialog} sx={{ '& .MuiPaper-root': { borderRadius: '12px', width: '100%' } }}>
        <div className="p-6 flex flex-col gap-6 w-full items-center">
          <p className="text-black-900 text-lg font-semibold leading-7 text-center">{t('RECEIVED_ORDER')}</p>
          <button
            className={`px-18 py-2.5 rounded-lg border border-solid shadow-button text-base font-semibold leading-6 bg-primary-green border-primary-green text-white`}
            onClick={handleCloseDialog}
          >
            {t('RECEIVED_ORDER_BUTTON')}
          </button>
        </div>
      </Dialog>
    </OrderContext.Provider>
  );
};
